import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { useDrawer } from '@/_shared/components';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { CollapsableCard } from '@/_shared/components/CollapsableCard';
import { StyledTableDrawer, usePaginatedTable } from '@/_shared/components/Table';
import { Routes } from '@/_shared/constants';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { GenericApiError } from '@/_shared/types/apiError';
import { getDepartmentTeamMembersCall, ResponseGetDepartmentTeams } from '@/departments/state'; // TODO use specific api
import { PAGE_SIZE_TEAM_MEMBERS } from '@/departments/state/constants';
import { LeadManager } from '../LeadManager';
import { MembersList } from '../MembersList';

type Props = {
  departmentId?: string;
  loading?: boolean;
  error?: GenericApiError | undefined;
  success?: boolean;
  responseGetDepartmentTeams?: ResponseGetDepartmentTeams;
};

export const TabDepartment: FC<Props> = ({
  departmentId,
  loading,
  error,
  success,
  responseGetDepartmentTeams,
}) => {
  const t = useTranslations('department.details');

  const { genericResponsiveValue } = useResponsiveStyle();

  const tShared = useTranslations();
  const navigate = useNavigate();
  const { isOpen, close, ...drawerProps } = useDrawer();

  const {
    execute: executeGetDepartmentTeamMembers,
    loading: loadingGetDepartmentTeamMembers,
    error: errorGetDepartmentTeamMembers,
    data,
    totalItems,
  } = getDepartmentTeamMembersCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    pageSize: PAGE_SIZE_TEAM_MEMBERS,
    totalItems: totalItems || 0,
  });

  const drawers: StyledTableDrawer['instances'] = [];

  const onOpenCollapsable = useCallback(
    (id: string) => {
      if (departmentId) {
        executeGetDepartmentTeamMembers({
          teamId: id,
          departmentId,
          excludeMemberType: ['departmentManager'],
        });
      }
    },
    [executeGetDepartmentTeamMembers, departmentId],
  );

  const onTeamNameClick = useCallback(
    (teamId: string) => {
      if (!departmentId) {
        return;
      }
      navigate(Routes.DepartmentTeamDetails.replace(':teamId', teamId));
    },
    [departmentId, navigate],
  );

  if (loading) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (error) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (success && !loading && !error) {
    return (
      <Box>
        <Card mt='m' mb='s' display='flex' flexDirection='column'>
          <Box
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: {
                desktop: 'row',
              },
              defaultValue: 'column',
            })}
            alignItems={genericResponsiveValue({
              breakPoints: {
                desktop: 'center',
              },
              defaultValue: 'flex-start',
            })}
          >
            <Typography variant='h1'>
              {responseGetDepartmentTeams?.department.name !== 'unknown'
                ? responseGetDepartmentTeams?.department.name
                : t('noDepartmentName')}
            </Typography>
            <Box
              ml={genericResponsiveValue({
                breakPoints: {
                  desktop: 'xs',
                },
                defaultValue: 'none',
              })}
              mt={genericResponsiveValue({
                breakPoints: {
                  desktop: 'none',
                },
                defaultValue: 'xs',
              })}
              alignItems='center'
              flexDirection='row'
              display='flex'
              flex={1}
            >
              <Svg name='users' size='s' />
              <Typography ml='xxs' variant='h1'>
                {responseGetDepartmentTeams?.department?.stats?.totalEmployees || 0}
              </Typography>
            </Box>
          </Box>

          <Box display='flex' flexDirection='column' mt='m'>
            <Typography variant='p2/label'>{t('departmentManager')}</Typography>
            <LeadManager
              loading={loadingGetDepartmentTeamMembers}
              error={errorGetDepartmentTeamMembers}
              paginedProps={{
                ...paginedProps,
                paginatedData: [responseGetDepartmentTeams?.department.manager],
              }}
              drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
              hidePagination
            />
          </Box>
        </Card>
        <Typography my='xs' variant='h2'>
          {t('team')}
        </Typography>
        {responseGetDepartmentTeams &&
          responseGetDepartmentTeams.data.map(team => (
            <CollapsableCard
              key={team.name}
              mb='xs'
              title={team.id === 'unknown' ? t('noTeamName') : team.name}
              dataTestIdButton='teams-collapsable-card-button'
              onCustomToggle={() => onOpenCollapsable(team.id)}
              titleProps={{
                onClick: () => (team.id !== 'unknown' ? onTeamNameClick(team.id) : null),
                style: { cursor: 'pointer' },
              }}
              closeOthersCollapseOnOpen
              onTitleHoverStyle={{ textDecoration: team.id !== 'unknown' ? 'underline' : 'none' }}
            >
              <Box
                width='100%'
                minWidth={0}
                minHeight={0}
                overflow={genericResponsiveValue({
                  breakPoints: { desktop: 'visible' },
                  defaultValue: 'auto',
                })}
                style={{ WebkitOverflowScrolling: 'touch' }}
              >
                <Box
                  alignItems='center'
                  mb='s'
                  display='flex'
                  flexDirection='row'
                  justifyContent='flex-end'
                >
                  <Box
                    display='flex'
                    width={genericResponsiveValue({
                      breakPoints: { desktop: 'auto' },
                      defaultValue: '100%',
                    })}
                    flexDirection={genericResponsiveValue({
                      breakPoints: { desktop: 'row' },
                      defaultValue: 'column',
                    })}
                    alignItems={genericResponsiveValue({
                      breakPoints: { desktop: 'center' },
                      defaultValue: 'flex-start',
                    })}
                    mt={genericResponsiveValue({
                      breakPoints: { desktop: 'xs' },
                      defaultValue: 's',
                    })}
                  >
                    <Typography variant='p3/label'>
                      {t('manager')}
                      <Typography variant='p2/light' as='span'>
                        {team.manager ? `${team.manager.firstName} ${team.manager.lastName}` : '-'}
                      </Typography>
                    </Typography>
                    <Box
                      ml={genericResponsiveValue({
                        breakPoints: { desktop: 'xl' },
                        defaultValue: 'none',
                      })}
                      alignItems='center'
                      flexDirection='row'
                      display='flex'
                      mt={genericResponsiveValue({
                        breakPoints: { desktop: 'none' },
                        defaultValue: 'xxs',
                      })}
                    >
                      <Svg name='users' size='s' />
                      <Typography ml='xxs' variant='h1'>
                        {totalItems || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <MembersList
                  loading={loadingGetDepartmentTeamMembers}
                  error={errorGetDepartmentTeamMembers}
                  paginedProps={paginedProps}
                  drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
                />
              </Box>
            </CollapsableCard>
          ))}
      </Box>
    );
  }

  return null;
};
